/**
 * Dashboard Events Hook
 * Custom hook for managing real-time dashboard events
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../../../contexts/AuthContext';
import DashboardEventService from '../services/DashboardEventService';

export const useDashboardEvents = (options = {}) => {
  const { 
    autoConnect = true,
    eventTypes = ['all'], // ['all'] or specific types like ['reservation-created', 'stats-updated']
    onEvent = null
  } = options;

  const { token, loading: authLoading } = useAuth();
  const [connected, setConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [lastEvent, setLastEvent] = useState(null);
  const [eventCount, setEventCount] = useState(0);
  const unsubscribeFunctions = useRef([]);

  // Handle individual events
  const handleEvent = useCallback((eventType, payload) => {
    // Skip reconnection events for the event counter
    if (eventType !== 'reconnection') {
      setLastEvent({ type: eventType, payload, timestamp: new Date() });
      setEventCount(prev => prev + 1);
    }
    
    if (onEvent) {
      onEvent(eventType, payload);
    }
  }, [onEvent]);

  // Handle connection status changes
  const handleConnectionEvent = useCallback((payload) => {
    const { status, error } = payload;
    setConnected(status === 'connected');
    setConnectionStatus(status);
    
    if (status === 'error') {
      console.error('Dashboard SSE connection error:', error);
    }
  }, []);

  // Connect to events
  const connect = useCallback(() => {
    if (!token || authLoading) return;

    // Clear existing subscriptions
    unsubscribeFunctions.current.forEach(unsubscribe => unsubscribe());
    unsubscribeFunctions.current = [];

    // Subscribe to connection events
    const connectionUnsub = DashboardEventService.subscribe('connection', handleConnectionEvent);
    unsubscribeFunctions.current.push(connectionUnsub);

    // Subscribe to requested event types
    eventTypes.forEach(eventType => {
      const unsub = DashboardEventService.subscribe(eventType, 
        eventType === 'all' ? handleEvent : (payload) => handleEvent(eventType, payload)
      );
      unsubscribeFunctions.current.push(unsub);
    });

    // Connect to SSE
    DashboardEventService.connect(token);
  }, [token, authLoading, handleEvent, handleConnectionEvent]); // eslint-disable-line react-hooks/exhaustive-deps

  // Disconnect from events
  const disconnect = useCallback(() => {
    unsubscribeFunctions.current.forEach(unsubscribe => unsubscribe());
    unsubscribeFunctions.current = [];
    DashboardEventService.disconnect();
    setConnected(false);
    setConnectionStatus('disconnected');
    // Reset the event counter on disconnect
    setEventCount(0);
  }, []);

  // Auto-connect when token is available
  useEffect(() => {
    if (autoConnect && token && !authLoading) {
      connect();
    }

    return () => {
      disconnect();
    };
    // Only depend on critical values, not the functions
  }, [autoConnect, token, authLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    connected,
    connectionStatus,
    lastEvent,
    eventCount,
    connect,
    disconnect,
    // Utility functions
    subscribe: DashboardEventService.subscribe.bind(DashboardEventService),
    emit: DashboardEventService.emit.bind(DashboardEventService),
    getConnectionStatus: DashboardEventService.getConnectionStatus.bind(DashboardEventService)
  };
};

// Specialized hooks for different event types
export const useReservationEvents = (onEvent) => {
  return useDashboardEvents({
    eventTypes: ['reservation-created', 'reservation-updated', 'reservation-cancelled'],
    onEvent
  });
};

export const useInventoryEvents = (onEvent) => {
  return useDashboardEvents({
    eventTypes: ['inventory-status-changed', 'inventory-updated'],
    onEvent
  });
};

export const usePaymentEvents = (onEvent) => {
  return useDashboardEvents({
    eventTypes: ['payment-processed', 'payment-failed'],
    onEvent
  });
};

export const useStatsEvents = (onEvent) => {
  return useDashboardEvents({
    eventTypes: ['stats-updated'],
    onEvent
  });
};